import { ChainType } from "@src/types/chainType"

export type WalletIdentity = `${ChainType}:${string}`

export function makeWalletIdentity(
  chainType: ChainType,
  address: string
): WalletIdentity {
  const normalized =
    chainType === ChainType.EVM ? address.toLowerCase() : address
  return `${chainType}:${normalized}`
}

/**
 * Build a WalletIdentity from the active session. Webauthn sessions key
 * off the persisted passkey publicKey rather than `sessionAddress`, which
 * can briefly drop during client-side navigation. Returns null when the
 * session isn't authorized or lacks the inputs we need.
 */
export function walletIdentityFromSession(input: {
  isAuthorized: boolean
  chainType: ChainType | null | undefined
  address: string | null | undefined
  webauthnPublicKey?: string | null
}): WalletIdentity | null {
  if (!input.isAuthorized) return null

  if (
    input.chainType === ChainType.WebAuthn &&
    input.webauthnPublicKey != null
  ) {
    return makeWalletIdentity(ChainType.WebAuthn, input.webauthnPublicKey)
  }

  if (input.chainType == null || input.address == null) return null

  return makeWalletIdentity(input.chainType, input.address)
}

const VALID_CHAIN_PREFIXES = new Set<string>(Object.values(ChainType))

/**
 * Parse a wallet identity from arbitrary input (e.g. URL path param) and
 * validate the chain prefix matches a known ChainType. Returns null on
 * invalid input.
 */
export function parseWalletIdentity(
  walletIdentity: string
): { chainType: ChainType; address: string } | null {
  const colonIdx = walletIdentity.indexOf(":")
  if (colonIdx <= 0 || colonIdx === walletIdentity.length - 1) {
    return null
  }

  const prefix = walletIdentity.slice(0, colonIdx)
  if (!VALID_CHAIN_PREFIXES.has(prefix)) return null
  const chainType = prefix as ChainType

  const address = walletIdentity.slice(colonIdx + 1)

  if (chainType === ChainType.EVM) {
    const lower = address.toLowerCase()
    return /^0x[a-f0-9]{40}$/.test(lower) ? { chainType, address: lower } : null
  }

  return { chainType, address }
}
